import React, { useState } from 'react';

import { Box, Loader, Paper, Stack, Text, TextInput, Title, UnstyledButton } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useDebouncedValue } from '@mantine/hooks';
import { IconSearch } from '@tabler/icons-react';
import useAutoCompletePackage from 'hooks/package/useAutoCompletePackage';

import { useGLMCardStyles } from '../styles';
import { FormValues } from '../types';

interface IKiloGLMPackageSelectProps {
  form: UseFormReturnType<FormValues, (values: FormValues) => FormValues>;
  type: 'create' | 'edit';
}

const KiloGLMPackageSelect = ({ form }: IKiloGLMPackageSelectProps): JSX.Element => {
  const { classes, cx } = useGLMCardStyles();
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 250);

  const { data, isLoading } = useAutoCompletePackage({ search: debouncedSearch });
  const packages = data?.data?.results?.map(p => ({ value: p.packageId, label: p.name })) ?? [];

  const togglePackage = (id: string, label: string): void => {
    const currentIds = form.values.packageIds || [];
    const currentLabels = form.values.packageLabels || {};
    if (currentIds.includes(id)) {
      const remainingLabels = { ...currentLabels };
      delete remainingLabels[id];
      form.setValues(prev => ({
        ...prev,
        packageIds: currentIds.filter(v => v !== id),
        packageLabels: remainingLabels,
      }));
    } else {
      form.setValues(prev => ({
        ...prev,
        packageIds: [...currentIds, id],
        packageLabels: { ...currentLabels, [id]: label },
      }));
    }
  };

  return (
    <Paper>
      <Stack sx={{ paddingTop: '20px' }}>
        <Title order={3}>Select Package</Title>
        <Text size={15}>Select the packages that trigger the flow when purchased.</Text>
        <TextInput
          placeholder='Search packages'
          value={search}
          onChange={e => setSearch(e.currentTarget.value)}
          icon={<IconSearch size={14} />}
          rightSection={isLoading ? <Loader size={14} /> : null}
        />
        <Stack spacing={6}>
          {packages.map(pkg => {
            const isSelected = (form.values.packageIds || []).includes(pkg.value);
            return (
              <UnstyledButton key={pkg.value} onClick={() => togglePackage(pkg.value, pkg.label)}>
                <Box className={cx(classes.card, { [classes.cardSelected]: isSelected })}>
                  <Text weight={isSelected ? 600 : 400}>{pkg.label}</Text>
                </Box>
              </UnstyledButton>
            );
          })}
          {!isLoading && packages.length === 0 && (
            <Text size='sm' color='dimmed'>
              No packages found
            </Text>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};

export default KiloGLMPackageSelect;
